import axios from 'axios';
import { nextTick } from 'vue';
import { createRouter, createWebHistory, START_LOCATION } from 'vue-router';
import eventBus from './event-bus';
import store, { sessionRehydrated } from './store';
import { API_ROOT } from './store/api';
import { DEBUG, handleGlobalError } from './store/constants';
import { trackPageView, trackAdvisoryPartnerHit } from '@/posthog_tracking';
import { ANIMATION_LENGTHS } from '@/constants';
import {
    FLAG_CLIENT_TASKS,
    FLAG_DOCUMENTS,
    FLAG_GIFT_TAX_QUESTIONNAIRE,
    FLAG_GIFT_TAX_QUESTIONNAIRE_ADVISORS,
    FLAG_GRAT_CALCULATOR_NEW_UI,
    FLAG_CALCULATORS_OPS_DASHBOARD,
    FLAG_OPS_DOCUMENT_VALIDATION,
    FLAG_OPS_FORM_5227_EXTRACTION,
    FLAG_OPS_CUSTODIAN_STATEMENTS,
    FLAG_TAX_SEASON_COLLECTION_ADVISORS,
    isFeatureEnabled,
} from '@/store/feature_flags';
import { isCalculatorRouteEnabled } from '@/services/calculator_flags';
import { PRODUCTS_VERSION_POLL_ROUTES } from '@/store/model-schema';
import { handOffToSite, siteRedirectTarget } from '@/services/sites';

const DesignSystem = () => import('./pages/admin/DesignSystem.vue').catch(handleGlobalError);
const ObservabilityTest = () => import('./pages/admin/ObservabilityTest.vue').catch(handleGlobalError);

const AdvisorClientDetails = () => import('./pages/advisor/ClientDetails.vue').catch(handleGlobalError);
const AdvisorClientManagement = () => import('./pages/advisor/ClientManagement.vue').catch(handleGlobalError);
const AdvisorClientTaxSeason = () => import('./pages/advisor/AdvisorClientTaxSeason.vue').catch(handleGlobalError);
const AdvisorClientGiftTax = () => import('./pages/advisor/AdvisorClientGiftTax.vue').catch(handleGlobalError);
const AdvisorGiftTaxDashboard = () => import('./pages/advisor/AdvisorGiftTaxDashboard.vue').catch(handleGlobalError);
const AdvisorDashboard = () => import('./pages/advisor/Dashboard.vue').catch(handleGlobalError);
const AdvisorClientDataUpload = () => import('./pages/advisor/ClientDataUpload.vue').catch(handleGlobalError);
const AdvisorSelectUser = () => import('./pages/advisor/SelectUser.vue').catch(handleGlobalError);
const AdvisorTeamManagement = () => import('./pages/advisor/TeamManagement.vue').catch(handleGlobalError);
const AdvisorTaxSeasonDashboard = () =>
    import('./pages/advisor/AdvisorTaxSeasonDashboard.vue').catch(handleGlobalError);
const AdvisorClientTaxSeasonDocuments = () =>
    import('./pages/advisor/AdvisorClientTaxSeasonDocuments.vue').catch(handleGlobalError);
const AdvisorWorkspaceOverview = () => import('./pages/advisor/WorkspaceOverview.vue').catch(handleGlobalError);
const AdvisorWorkspaceSettings = () => import('./pages/advisor/WorkspaceSettings.vue').catch(handleGlobalError);

const Calculators = () => import('./pages/Calculators.vue').catch(handleGlobalError);
const Dashboard = () => import('./pages/Dashboard.vue').catch(handleGlobalError);
const Documents = () => import('./pages/Documents.vue').catch(handleGlobalError);
const GiftTaxQuestionnaire = () => import('./pages/GiftTaxQuestionnaire.vue').catch(handleGlobalError);
const GuidedPlanner = () => import('./pages/GuidedPlanner.vue').catch(handleGlobalError);
const GuidedPlannerFlow = () => import('./pages/GuidedPlannerFlow.vue').catch(handleGlobalError);
const Home = () => import('./pages/Home.vue').catch(handleGlobalError);
const Projects = () => import('./pages/Projects.vue').catch(handleGlobalError);
const Scenarios = () => import('./pages/Scenarios.vue').catch(handleGlobalError);

const OnboardingWizard = () => import('./pages/onboarding/OnboardingWizard.vue').catch(handleGlobalError);
const OnboardingDetails = () => import('./pages/onboarding/OnboardingDetails.vue').catch(handleGlobalError);
const OnboardingForm = () => import('./pages/onboarding/OnboardingProfile.vue').catch(handleGlobalError);
const OnboardingInitiate = () => import('./pages/onboarding/OnboardingInitiate.vue').catch(handleGlobalError);
const OnboardingStrategy = () => import('./pages/onboarding/OnboardingStrategy.vue').catch(handleGlobalError);
const DocumentReview = () => import('./pages/onboarding/DocumentReview.vue').catch(handleGlobalError);
const ProjectDetail = () => import('./pages/onboarding/ProjectDetail.vue').catch(handleGlobalError);
const TrustAgreementReview = () => import('./pages/onboarding/TrustAgreementReview.vue').catch(handleGlobalError);
const TrustAgreementSign = () => import('./pages/onboarding/TrustAgreementSign.vue').catch(handleGlobalError);

const MaterialParticipationLog = () => import('./pages/MaterialParticipationLog.vue').catch(handleGlobalError);
const SolarProjects = () => import('./pages/SolarProjects.vue').catch(handleGlobalError);
const SolarProjectsSaved = () => import('./pages/SolarProjectsSaved.vue').catch(handleGlobalError);
const SolarProjectDetails = () => import('./pages/SolarProjectDetails.vue').catch(handleGlobalError);

const CalculatorClat = () => import('./pages/model/CalculatorClat.vue').catch(handleGlobalError);
const CalculatorCrut = () => import('./pages/model/CalculatorCrut.vue').catch(handleGlobalError);
const CalculatorCrutReport = () => import('./pages/model/CalculatorCrutReport.vue').catch(handleGlobalError);
const CalculatorCrutTestCases = () => import('./pages/model/CalculatorCrutTestCases.vue').catch(handleGlobalError);
// The rebuilt GRAT page (ENG-3351) behind `grat-calculator-new-ui`; off serves the page
// production shipped with. Decided here, not in a wrapper component: Vue Router reads
// `beforeRouteLeave` off the raw page options, so a wrapper would lose the unsaved-run
// prompt. The router resolves a lazy route component once per page load, so a flag
// flipped while the app is open takes effect on the next load, like the other gates.
const CalculatorGrat = () =>
    (isFeatureEnabled(FLAG_GRAT_CALCULATOR_NEW_UI)
        ? import('./pages/model/CalculatorGrat.vue')
        : import('./pages/model/CalculatorGratLegacy.vue')
    ).catch(handleGlobalError);
const CalculatorIra = () => import('./pages/model/CalculatorIra.vue').catch(handleGlobalError);
const CalculatorOilGas = () => import('./pages/model/CalculatorOilGas.vue').catch(handleGlobalError);
const CalculatorPpli = () => import('./pages/model/CalculatorPpli.vue').catch(handleGlobalError);
const CalculatorQsbs = () => import('./pages/model/CalculatorQsbs.vue').catch(handleGlobalError);
const CalculatorSolar = () => import('./pages/model/CalculatorSolar.vue').catch(handleGlobalError);
const CalculatorNgt = () => import('./pages/model/CalculatorNgt.vue').catch(handleGlobalError);
const CalculatorEstateBurn = () => import('./pages/model/CalculatorEstateTax.vue').catch(handleGlobalError);
const CalculatorComparisonEstate = () =>
    import('./pages/model/CalculatorComparisonEstate.vue').catch(handleGlobalError);
const CalculatorComparisonCapitalGains = () =>
    import('./pages/model/CalculatorComparisonCapitalGains.vue').catch(handleGlobalError);
const CalculatorComparisonOrdinaryIncome = () =>
    import('./pages/model/CalculatorComparisonOrdinaryIncome.vue').catch(handleGlobalError);

const Activate = () => import('./pages/Activate.vue').catch(handleGlobalError);
const ActivationResend = () => import('./pages/ActivationResend.vue').catch(handleGlobalError);
const ForgotPassword = () => import('./pages/ForgotPassword.vue').catch(handleGlobalError);
const Login = () => import('./pages/Login.vue').catch(handleGlobalError);
const Messages = () => import('./pages/Messages.vue').catch(handleGlobalError);
const PasswordReset = () => import('./pages/PasswordReset.vue').catch(handleGlobalError);
const Profile = () => import('./pages/Profile.vue').catch(handleGlobalError);
const Register = () => import('./pages/Register.vue').catch(handleGlobalError);
const OilGasQuestionnaire = () => import('./pages/OilGasQuestionnaire.vue').catch(handleGlobalError);
const RiskAnalysis = () => import('./pages/RiskAnalysis.vue').catch(handleGlobalError);
const Tasks = () => import('./pages/Tasks.vue').catch(handleGlobalError);
const TaxSeasonDocuments = () => import('./pages/TaxSeasonDocuments.vue').catch(handleGlobalError);
const TaxSeasonInfo = () => import('./pages/TaxSeasonInfo.vue').catch(handleGlobalError);

const PrivacyPolicy = () => import('./pages/PrivacyPolicy.vue').catch(handleGlobalError);
const TermsOfService = () => import('./pages/TermsOfService.vue').catch(handleGlobalError);

const Operations = () => import('./pages/admin/Operations.vue').catch(handleGlobalError);
const PartnerUsage = () => import('./pages/admin/PartnerUsage.vue').catch(handleGlobalError);
const PlatformSettings = () => import('./pages/admin/PlatformSettings.vue').catch(handleGlobalError);
const CalculatorsDashboard = () => import('./pages/admin/CalculatorsDashboard.vue').catch(handleGlobalError);
const DocumentValidationReview = () => import('./pages/admin/DocumentValidationReview.vue').catch(handleGlobalError);
const Form5227ReviewQueue = () => import('./pages/admin/Form5227ReviewQueue.vue').catch(handleGlobalError);
const Form5227ReviewUpload = () => import('./pages/admin/Form5227ReviewUpload.vue').catch(handleGlobalError);
const Form5227ReviewWorkspace = () => import('./pages/admin/Form5227ReviewWorkspace.vue').catch(handleGlobalError);
const StatementReviewQueue = () => import('./pages/admin/StatementReviewQueue.vue').catch(handleGlobalError);
const StatementReviewUpload = () => import('./pages/admin/StatementReviewUpload.vue').catch(handleGlobalError);
const StatementReviewWorkspace = () => import('./pages/admin/StatementReviewWorkspace.vue').catch(handleGlobalError);
const AdminCreateDistributionReport = () =>
    import('./pages/admin/CreateDistributionReport.vue').catch(handleGlobalError);
const AdminUserProfile = () => import('./pages/admin/UserProfile.vue').catch(handleGlobalError);
const AdminOnboardingTrustAgreementEmail = () =>
    import('./pages/admin/OnboardingTrustAgreementEmail.vue').catch(handleGlobalError);
const AdminOnboardingEditTrustAgreement = () =>
    import('./pages/admin/OnboardingEditTrustAgreement.vue').catch(handleGlobalError);
const AdminTaxTableGenerator = () => import('./pages/admin/TaxTableGenerator.vue').catch(handleGlobalError);

const path = DEBUG ? '/' : import.meta.env.BASE_URL || '/';

let hasTrackedAdvisoryPartner = false;

// Shared by both client-documents entries (the advisor path and the
// operations path). Staff see every client; advisors only their attached
// clients; everyone else is bounced. The advisor check probes the accounts
// endpoint (403 for unattached clients) BEFORE entering: the page's own
// fetches run through logApiError, whose blanket 401/403 handling would log
// the advisor out entirely.
async function clientDocumentsGuard(to) {
    // Documents kill-switch: when the feature is off, no one reaches the
    // manager (advisor/ops) document views either. Home resolves to the
    // right landing per persona.
    if (!isFeatureEnabled(FLAG_DOCUMENTS)) {
        return '/';
    }
    if (!store.getters.isLoggedIn) {
        return { name: 'login', query: { next: to.path } };
    }
    const user = store.getters.user;
    if (user && (user.is_superuser || user.is_staff)) {
        return undefined;
    }
    if (!store.getters.isAdvisor) {
        return '/';
    }
    try {
        await axios.get(`${API_ROOT}/accounts/${to.params.clientId}/`, store.getters.axiosConfig);
        return undefined;
    } catch {
        eventBus.emit('notification::error', "You don't have access to this client's documents.");
        return { name: 'advisor-client-management' };
    }
}

// Calculator kill switches (ENG-2841): every calculator route — including
// /shared/ links and the hub — bounces home when its flag (or the whole
// `calculators` domain) is off. Home resolves per persona and is safe for
// anonymous visitors, which most calculator traffic is.
function calculatorRouteGuard(to) {
    if (!isCalculatorRouteEnabled(to.name)) {
        return '/';
    }
}

const router = createRouter({
    history: createWebHistory(path),
    linkExactActiveClass: 'active',
    routes: [
        {
            path: '/:pathMatch(.*)*',
            name: 'catchall',
            component: Login,
        },
        {
            path: '/',
            name: 'home',
            component: Home,
            meta: {
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com',
                    },
                ],
            },
        },
        {
            path: '/clear/',
            name: 'clear',
            beforeEnter() {
                store.dispatch('logout');
                window.localStorage.clear();
                window.location.href = '/';
            },
        },

        {
            path: '/activate/:uid/:token/',
            name: 'activate',
            component: Activate,
            meta: {
                title: 'Activate',
                metaTags: [{ name: 'referrer', content: 'no-referrer' }],
            },
        },
        {
            path: '/login/',
            name: 'login',
            component: Login,
            meta: {
                title: 'Welcome to Valur', // TODO: Respect WLP name
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/login',
                    },
                ],
            },
        },
        {
            path: '/privacy-policy/',
            name: 'privacy-policy',
            component: PrivacyPolicy,
            meta: {
                title: 'Privacy Policy',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/privacy-policy',
                    },
                ],
            },
        },
        {
            path: '/password-reset/:uid/:token/',
            name: 'password-reset',
            component: PasswordReset,
            meta: {
                title: 'Reset password',
                metaTags: [{ name: 'referrer', content: 'no-referrer' }],
            },
        },
        {
            path: '/terms-of-service/',
            name: 'terms-of-service',
            component: TermsOfService,
            meta: {
                title: 'Terms of Service',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/terms-of-service',
                    },
                ],
            },
        },

        {
            path: '/dashboard/',
            name: 'dashboard',
            component: Dashboard,
            meta: {
                title: 'Dashboard',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/dashboard',
                    },
                ],
            },
        },
        {
            path: '/projects/',
            name: 'projects',
            component: Projects,
            meta: {
                title: 'Projects',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/projects',
                    },
                ],
            },
        },
        {
            path: '/scenarios/',
            name: 'scenarios',
            component: Scenarios,
            meta: {
                title: 'Scenarios',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/scenarios',
                    },
                ],
            },
        },

        {
            path: '/advisor-client-details/:id',
            name: 'advisor-client-details',
            component: AdvisorClientDetails,
            meta: { title: 'Advisor - Client details' },
        },
        {
            path: '/advisor-client-tax-season/:clientId',
            name: 'advisor-client-tax-season',
            component: AdvisorClientTaxSeason,
            meta: { title: 'Advisor - Client Tax Season' },
            beforeEnter() {
                if (!isFeatureEnabled(FLAG_TAX_SEASON_COLLECTION_ADVISORS)) {
                    return { name: 'advisor-dashboard' };
                }
            },
        },
        {
            path: '/advisor-client-tax-season-documents/:clientId',
            name: 'advisor-client-tax-season-documents',
            component: AdvisorClientTaxSeasonDocuments,
            meta: { title: 'Advisor - Client Tax Season Documents' },
            beforeEnter() {
                if (!isFeatureEnabled(FLAG_TAX_SEASON_COLLECTION_ADVISORS)) {
                    return { name: 'advisor-dashboard' };
                }
            },
        },
        {
            path: '/advisor-client-gift-tax/:clientId',
            name: 'advisor-client-gift-tax',
            component: AdvisorClientGiftTax,
            meta: { title: 'Advisor - Client Gift Tax Questionnaire' },
            beforeEnter() {
                // Both flags required: the base flag is the seasonal kill switch, and
                // the backend refuses advisor requests unless both are on.
                const enabled =
                    isFeatureEnabled(FLAG_GIFT_TAX_QUESTIONNAIRE) &&
                    isFeatureEnabled(FLAG_GIFT_TAX_QUESTIONNAIRE_ADVISORS);
                if (!enabled) {
                    return { name: 'advisor-dashboard' };
                }
            },
        },
        {
            path: '/advisor-gift-tax-dashboard/',
            name: 'advisor-gift-tax-dashboard',
            component: AdvisorGiftTaxDashboard,
            meta: { title: 'Advisor - Gift Tax Dashboard' },
            beforeEnter() {
                // Both flags required (see advisor-client-gift-tax).
                const enabled =
                    isFeatureEnabled(FLAG_GIFT_TAX_QUESTIONNAIRE) &&
                    isFeatureEnabled(FLAG_GIFT_TAX_QUESTIONNAIRE_ADVISORS);
                if (!enabled) {
                    return { name: 'advisor-dashboard' };
                }
            },
        },
        {
            path: '/advisor-client-management/',
            name: 'advisor-client-management',
            component: AdvisorClientManagement,
            meta: {
                title: 'Advisor - Client management',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/advisor-client-management',
                    },
                ],
            },
        },
        {
            path: '/advisor-dashboard/',
            name: 'advisor-dashboard',
            component: AdvisorDashboard,
            meta: {
                title: 'Advisor - Dashboard',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/advisor-dashboard',
                    },
                ],
            },
        },
        {
            path: '/advisor-client-data-upload/',
            name: 'advisor-client-data-upload',
            component: AdvisorClientDataUpload,
            meta: {
                title: 'Advisor - Upload Client Data',
            },
        },
        {
            path: '/advisor-tax-season-dashboard/',
            name: 'advisor-tax-season-dashboard',
            component: AdvisorTaxSeasonDashboard,
            meta: {
                title: 'Advisor - Tax Season Dashboard',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/advisor-tax-season-dashboard',
                    },
                ],
            },
            beforeEnter() {
                if (!isFeatureEnabled(FLAG_TAX_SEASON_COLLECTION_ADVISORS)) {
                    return { name: 'advisor-dashboard' };
                }
            },
        },
        {
            path: '/select-user/',
            name: 'select-user',
            component: AdvisorSelectUser,
            meta: {
                title: 'Advisor - Select user',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/select-user',
                    },
                ],
            },
        },
        {
            path: '/advisor/workspace/members/',
            name: 'advisor-team-management',
            component: AdvisorTeamManagement,
            meta: {
                title: 'Advisor - Team management',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/advisor/workspace/members',
                    },
                ],
            },
        },
        // Legacy path — bookmarks/links predating the workspace URL scheme.
        {
            path: '/advisor-team-management/',
            redirect: '/advisor/workspace/members/',
        },
        {
            path: '/advisor-documents/',
            name: 'advisor-documents',
            component: Documents,
            meta: { title: 'Advisor - Documents', viewType: 'advisor' },
            beforeEnter() {
                if (!isFeatureEnabled(FLAG_DOCUMENTS)) {
                    return { name: 'advisor-dashboard' };
                }
            },
        },
        {
            path: '/advisor-projects/:onboarding_id?/',
            name: 'advisor-projects',
            component: SolarProjects,
            meta: { title: 'Advisor - Projects', viewType: 'advisor' },
        },
        {
            path: '/advisor-workspace-overview/',
            name: 'advisor-workspace-overview',
            component: AdvisorWorkspaceOverview,
            meta: { title: 'Advisor - Workspace Overview' },
        },
        {
            path: '/advisor-workspace-settings/',
            name: 'advisor-workspace-settings',
            component: AdvisorWorkspaceSettings,
            meta: { title: 'Advisor - Settings' },
        },
        // Merged into the unified Profile page; kept as a redirect for old bookmarks.
        {
            path: '/advisor-profile/',
            redirect: { name: 'profile' },
        },

        {
            path: '/admin/design-system/',
            name: 'design-system',
            component: DesignSystem,
            meta: {
                title: 'Design system',
                requiresAdmin: true,
            },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && user.is_superuser)) {
                    return '/';
                }
            },
        },
        {
            path: '/admin/observability-test/',
            name: 'observability-test',
            component: ObservabilityTest,
            meta: {
                title: 'Observability Test',
                requiresAdmin: true,
            },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && user.is_superuser)) {
                    return '/';
                }
            },
        },
        {
            path: '/tasks/',
            name: 'tasks',
            component: Tasks,
            meta: {
                title: 'Tasks',
            },
            beforeEnter() {
                // Backend refuses /tasks/ calls when the flag is off; the route
                // shouldn't be reachable then either (nav hides it — this covers
                // deep links).
                if (!isFeatureEnabled(FLAG_CLIENT_TASKS)) {
                    return { name: 'dashboard' };
                }
            },
        },
        {
            path: '/documents/',
            name: 'documents',
            component: Documents,
            meta: {
                title: 'Documents',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/documents',
                    },
                ],
            },
            beforeEnter() {
                if (!isFeatureEnabled(FLAG_DOCUMENTS)) {
                    return { name: 'dashboard' };
                }
            },
        },
        {
            // Manager mode of the documents page: advisors browse and manage a
            // client's tree (upload on behalf, ownership + visibility toggles).
            path: '/client-documents/:clientId',
            name: 'client-documents',
            component: Documents,
            meta: { title: 'Client documents' },
            beforeEnter: clientDocumentsGuard,
        },
        {
            // Same manager mode reached from Operations → Users (admin context).
            // A separate named route so the page can derive chrome + breadcrumbs
            // from how it was entered.
            path: '/operations/users/:clientId/documents/',
            name: 'operations-client-documents',
            component: Documents,
            meta: { title: 'Client documents' },
            beforeEnter: clientDocumentsGuard,
        },
        {
            // The page was Operations → Clients until ENG-3439; its links still work.
            path: '/operations/clients/:clientId/documents/',
            redirect: to => `/operations/users/${to.params.clientId}/documents/`,
        },
        {
            path: '/calculators/',
            name: 'calculators',
            beforeEnter: calculatorRouteGuard,
            component: Calculators,
            meta: {
                title: 'Calculators',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/calculators',
                    },
                ],
            },
        },
        {
            path: '/resend-activation/',
            name: 'resend-activation',
            component: ActivationResend,
            meta: {
                title: 'Resend activation',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/resend-activation',
                    },
                ],
            },
        },
        {
            path: '/forgot-password/',
            name: 'forgot-password',
            component: ForgotPassword,
            meta: {
                title: 'Forgot password',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/forgot-password',
                    },
                ],
            },
        },
        {
            path: '/guided-planner/',
            name: 'guided-planner',
            component: GuidedPlanner,
            meta: {
                title: 'Guided Financial Planning Tool | Valur Tax Planning Tool',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            "Get personalized tax and estate strategies with Valur's guided planner. " +
                            'Minimize taxes and plan your future with confidence.',
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            "Get personalized tax and estate strategies with Valur's guided planner. " +
                            'Minimize taxes and plan your future with confidence.',
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/guided-planner.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Guided Financial Planning Tool | Valur Tax Planning Tool',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/guided-planner',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Guided Financial Planning Tool | Valur Tax Planning Tool',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            "Get personalized tax and estate strategies with Valur's guided planner. " +
                            'Minimize taxes and plan your future with confidence.',
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/guided-planner.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/guided-planner',
                    },
                ],
            },
        },
        {
            path: '/guided-planner-flow/',
            name: 'guided-planner-flow',
            component: GuidedPlannerFlow,
            meta: {
                title: 'Guided Financial Planning Flow | Valur Tax Planning Tool',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Tailored tax optimization, estate planning, and wealth strategies—all in one ' +
                            'tool from Valur.',
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Tailored tax optimization, estate planning, and wealth strategies—all in one ' +
                            'tool from Valur.',
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/guided-planner-flow.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Guided Financial Planning Flow | Valur Tax Planning Tool',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/guided-planner-flow',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Guided Financial Planning Flow | Valur Tax Planning Tool',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Tailored tax optimization, estate planning, and wealth strategies—all in one ' +
                            'tool from Valur.',
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/guided-planner-flow.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/guided-planner-flow',
                    },
                ],
            },
        },
        {
            path: '/messages/',
            name: 'messages',
            component: Messages,
            meta: {
                title: 'Messages',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/messages',
                    },
                ],
            },
        },
        {
            path: '/profile/',
            name: 'profile',
            component: Profile,
            meta: {
                title: 'Profile',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/profile',
                    },
                ],
            },
        },
        {
            path: '/register/',
            name: 'register',
            component: Register,
            meta: {
                title: 'Register',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/register',
                    },
                ],
            },
        },
        {
            path: '/onboarding/',
            name: 'onboarding',
            component: OnboardingWizard,
            meta: { title: "Let's find the best ways to save" },
        },
        // Legacy path — the role-selection page grew into the full onboarding wizard.
        {
            path: '/role-selection/',
            redirect: { name: 'onboarding' },
        },
        {
            path: '/register/:uid?/:token?',
            name: 'register-advisor-link',
            component: Register,
            meta: {
                title: 'Register',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/register',
                    },
                ],
            },
        },
        {
            path: '/tax-season-info/',
            name: 'tax-season-info',
            component: TaxSeasonInfo,
            meta: {
                title: 'Tax Season Information',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/tax-season-info',
                    },
                ],
            },
        },
        {
            path: '/tax-season-documents/',
            name: 'tax-season-documents',
            component: TaxSeasonDocuments,
            meta: {
                title: 'Tax Season Documents',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/tax-season-documents',
                    },
                ],
            },
        },
        {
            path: '/gift-tax-questionnaire/',
            name: 'gift-tax-questionnaire',
            component: GiftTaxQuestionnaire,
            meta: {
                title: 'Gift Tax Questionnaire',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/gift-tax-questionnaire',
                    },
                ],
            },
            beforeEnter() {
                if (!isFeatureEnabled(FLAG_GIFT_TAX_QUESTIONNAIRE)) {
                    return { name: 'dashboard' };
                }
            },
        },

        // Trust
        {
            path: '/trust-agreement-sign/',
            name: 'trust-agreement-sign',
            component: TrustAgreementSign,
            meta: {
                title: 'Trust agreement sign',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/trust-agreement-sign',
                    },
                ],
            },
        },
        {
            path: '/trust-agreement-review/',
            name: 'trust-agreement-review',
            component: TrustAgreementReview,
            meta: {
                title: 'Trust agreement review',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/trust-agreement-review',
                    },
                ],
            },
        },
        {
            path: '/document-review/:onboarding_id/',
            name: 'document-review',
            component: DocumentReview,
            meta: { title: 'Document review' },
        },
        {
            path: '/project/:onboarding_id/',
            name: 'project-detail',
            component: ProjectDetail,
            meta: { title: 'Project' },
        },

        // Model
        {
            // Public share link — resolves a separately minted share token, distinct from the
            // owner-only :scenario_id URL below. Allowlisted in needsLogin(); noindex keeps
            // shared runs out of search engines.
            path: '/charitable-remainder-trust-calculator/shared/:share_token([0-9a-fA-F-]{36})',
            name: 'charitable-remainder-trust-calculator-shared',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorCrut,
            meta: {
                title: 'Charitable Remainder Trust Calculator | Valur Tax Planning Calculator',
                metaTags: [{ name: 'robots', content: 'noindex' }],
            },
        },
        {
            // Optional UUID param carries a saved scenario — the owner-only direct link
            // (share links use the /shared/:share_token sibling above). Constrained to a
            // UUID so it can't shadow the sibling `/test_cases` route below.
            path: '/charitable-remainder-trust-calculator/:scenario_id([0-9a-fA-F-]{36})?',
            name: 'charitable-remainder-trust-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorCrut,
            meta: {
                title: 'Charitable Remainder Trust Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Estimate Charitable Remainder Trust tax savings, lifetime income, and charitable ' +
                            'impact with Valur’s free calculator. Optimize taxes and giving today.',
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Estimate Charitable Remainder Trust tax savings, lifetime income, and charitable ' +
                            'impact with Valur’s free calculator. Optimize taxes and giving today.',
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/charitable-remainder-trust-calculator.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Charitable Remainder Trust Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/charitable-remainder-trust-calculator',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Charitable Remainder Trust Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Estimate Charitable Remainder Trust tax savings, lifetime income, and charitable ' +
                            'impact with Valur’s free calculator. Optimize taxes and giving today.',
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/charitable-remainder-trust-calculator.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/charitable-remainder-trust-calculator',
                    },
                ],
            },
        },
        {
            path: '/charitable-remainder-trust-report/',
            name: 'charitable-remainder-trust-report',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorCrutReport,
            meta: {
                title: 'Charitable Remainder Trust Report | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'View your Charitable Remainder Trust analysis. Discover tax savings, income ' +
                            "potential, and charitable impact with Valur's planning insights.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'View your Charitable Remainder Trust analysis. Discover tax savings, income ' +
                            "potential, and charitable impact with Valur's planning insights.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/charitable-remainder-trust-report.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Charitable Remainder Trust Report | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/charitable-remainder-trust-report',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Charitable Remainder Trust Report | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'View your Charitable Remainder Trust analysis. Discover tax savings, income ' +
                            "potential, and charitable impact with Valur's planning insights.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/charitable-remainder-trust-report.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/charitable-remainder-trust-report',
                    },
                ],
            },
        },
        {
            path: '/charitable-remainder-trust-calculator/test_cases',
            name: 'charitable-remainder-trust-calculator-test-cases',
            component: CalculatorCrutTestCases,
            meta: {
                title: 'CRUT Calculator Test Cases',
                requiresAdmin: true,
            },
            beforeEnter(to) {
                const blocked = calculatorRouteGuard(to);
                if (blocked) {
                    return blocked;
                }
                const user = store.getters.user;
                if (!(user && user.is_superuser)) {
                    return '/charitable-remainder-trust-calculator';
                }
            },
        },
        {
            path: '/compare-capital-gains-tax-savings/',
            name: 'compare-capital-gains-tax-savings',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorComparisonCapitalGains,
            meta: {
                title: 'Capital Gains Tax Savings Calculator | Valur Tax Comparison Tool',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Compare capital gains tax strategies to reduce taxes and keep more of your ' +
                            "profits. Use Valur's free tool today.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Compare capital gains tax strategies to reduce taxes and keep more of your ' +
                            "profits. Use Valur's free tool today.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/compare-capital-gains-tax-savings.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Capital Gains Tax Savings Calculator | Valur Tax Comparison Tool',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/compare-capital-gains-tax-savings',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Capital Gains Tax Savings Calculator | Valur Tax Comparison Tool',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Compare capital gains tax strategies to reduce taxes and keep more of your ' +
                            "profits. Use Valur's free tool today.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/compare-capital-gains-tax-savings.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/compare-capital-gains-tax-savings',
                    },
                ],
            },
        },
        {
            path: '/compare-ordinary-income-tax-savings/',
            name: 'compare-ordinary-income-tax-savings',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorComparisonOrdinaryIncome,
            meta: {
                title: 'Ordinary Income Tax Savings Calculator | Valur Tax Comparison Tool',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Compare strategies to lower ordinary income taxes. Build a more tax-efficient ' +
                            "plan with Valur's free tool.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Compare strategies to lower ordinary income taxes. Build a more tax-efficient ' +
                            "plan with Valur's free tool.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/compare-ordinary-income-tax-savings.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Ordinary Income Tax Savings Calculator | Valur Tax Comparison Tool',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/compare-ordinary-income-tax-savings',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Ordinary Income Tax Savings Calculator | Valur Tax Comparison Tool',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Compare strategies to lower ordinary income taxes. Build a more tax-efficient ' +
                            "plan with Valur's free tool.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/compare-ordinary-income-tax-savings.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/compare-ordinary-income-tax-savings',
                    },
                ],
            },
        },
        {
            path: '/compare-estate-strategies/',
            name: 'compare-estate-tax-savings',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorComparisonEstate,
            meta: {
                title: 'Estate Tax Savings Comparison Tool | Valur Tax Comparison Tool',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            "Compare estate tax strategies with Valur's free tool. Reduce taxes, maximize " +
                            'wealth transfer, and protect your legacy.',
                    },
                    {
                        name: 'robots',
                        content: 'noindex, follow',
                    },

                    {
                        property: 'og:description',
                        content:
                            "Compare estate tax strategies with Valur's free tool. Reduce taxes, maximize " +
                            'wealth transfer, and protect your legacy.',
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/compare-estate-tax-savings.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Estate Tax Savings Comparison Tool | Valur Tax Comparison Tool',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/compare-estate-strategies',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Estate Tax Savings Comparison Tool | Valur Tax Comparison Tool',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            "Compare estate tax strategies with Valur's free tool. Reduce taxes, maximize " +
                            'wealth transfer, and protect your legacy.',
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/compare-estate-tax-savings.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/compare-estate-strategies',
                    },
                ],
            },
        },
        {
            // Public share link (see the CRUT -shared route for the pattern).
            path: '/clat-calculator/shared/:share_token([0-9a-fA-F-]{36})',
            name: 'clat-calculator-shared',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorClat,
            meta: {
                title: 'Charitable Lead Annuity Trust Calculator | Valur Tax Planning Calculator',
                metaTags: [{ name: 'robots', content: 'noindex' }],
            },
        },
        {
            path: '/clat-calculator/:scenario_id([0-9a-fA-F-]{36})?',
            name: 'clat-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorClat,
            meta: {
                title: 'Charitable Lead Annuity Trust Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Estimate Charitable Lead Annuity Trust benefits. See tax savings, charitable ' +
                            "payouts, and wealth transfer potential with Valur's calculator.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Estimate Charitable Lead Annuity Trust benefits. See tax savings, charitable ' +
                            "payouts, and wealth transfer potential with Valur's calculator.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/clat-calculator.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Charitable Lead Annuity Trust Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/clat-calculator',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Charitable Lead Annuity Trust Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Estimate Charitable Lead Annuity Trust benefits. See tax savings, charitable ' +
                            "payouts, and wealth transfer potential with Valur's calculator.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/clat-calculator.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/clat-calculator',
                    },
                ],
            },
        },
        {
            path: '/ngt-calculator/',
            name: 'ngt-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorNgt,
            meta: {
                title: 'Non-Grantor Trust Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Estimate Non-Grantor Trust benefits for estate planning. See potential ' +
                            "tax savings and wealth transfer optimization with Valur's calculator.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },
                    {
                        property: 'og:description',
                        content:
                            'Estimate Non-Grantor Trust benefits for estate planning. See potential ' +
                            "tax savings and wealth transfer optimization with Valur's calculator.",
                    },
                    {
                        property: 'og:title',
                        content: 'Non-Grantor Trust Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/ngt-calculator',
                    },
                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Non-Grantor Trust Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Estimate Non-Grantor Trust benefits for estate planning. See potential ' +
                            "tax savings and wealth transfer optimization with Valur's calculator.",
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/ngt-calculator',
                    },
                ],
            },
        },
        {
            path: '/estate-tax-calculator/',
            name: 'estate-tax-burn-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorEstateBurn,
            meta: {
                title: 'Estate Tax Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Use our free estate tax calculator to estimate your federal and ' +
                            'state estate taxes, and see how to reduce the bill your heirs will face.',
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },
                    {
                        property: 'og:description',
                        content:
                            'Estimate your federal and state estate taxes and see how to reduce ' +
                            "the bill your heirs will face with Valur's free calculator.",
                    },
                    {
                        property: 'og:title',
                        content: 'Estate Tax Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/estate-tax-calculator',
                    },
                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Estate Tax Calculator | Valur Tax Planning Calculator',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/estate-tax-calculator',
                    },
                ],
            },
        },
        {
            path: '/estate-tax-burn-calculator/',
            redirect: { name: 'estate-tax-burn-calculator' },
        },
        {
            path: '/compare-estate-tax-savings/',
            redirect: { name: 'estate-tax-burn-calculator' },
        },
        {
            // Public share link (see the CRUT -shared route for the pattern).
            path: '/grat-calculator/shared/:share_token([0-9a-fA-F-]{36})',
            name: 'grat-calculator-shared',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorGrat,
            meta: {
                title: 'Grantor Retained Annuity Trust Calculator | Valur Tax Planning Calculator',
                metaTags: [{ name: 'robots', content: 'noindex' }],
            },
        },
        {
            path: '/grat-calculator/:scenario_id([0-9a-fA-F-]{36})?',
            name: 'grat-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorGrat,
            meta: {
                title: 'Grantor Retained Annuity Trust Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Estimate Grantor Retained Annuity Trust tax savings. Reduce gift taxes and ' +
                            "optimize wealth transfer with Valur's calculator.",
                    },
                    {
                        name: 'keywords',
                        content:
                            'grat calculator, grantor retained annuity trust calculator, how to calculate ' +
                            'grat annuity payment, annuity calculator excel, zeroed out grat calculator',
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Estimate Grantor Retained Annuity Trust tax savings. Reduce gift taxes and ' +
                            "optimize wealth transfer with Valur's calculator.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/grat-calculator.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Grantor Retained Annuity Trust Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/grat-calculator',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Grantor Retained Annuity Trust Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Estimate Grantor Retained Annuity Trust tax savings. Reduce gift taxes and ' +
                            "optimize wealth transfer with Valur's calculator.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/grat-calculator.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/grat-calculator',
                    },
                ],
            },
        },
        {
            path: '/ira-calculator/',
            name: 'ira-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorIra,
            meta: {
                title: 'IRA Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Calculate IRA growth and tax benefits. Optimize contributions and plan for ' +
                            "retirement with Valur's free calculator.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Calculate IRA growth and tax benefits. Optimize contributions and plan for ' +
                            "retirement with Valur's free calculator.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/ira-calculator.png',
                    },
                    {
                        property: 'og:title',
                        content: 'IRA Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/ira-calculator',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'IRA Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Calculate IRA growth and tax benefits. Optimize contributions and plan for ' +
                            "retirement with Valur's free calculator.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/ira-calculator.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/ira-calculator',
                    },
                ],
            },
        },
        {
            path: '/model-calculator/',
            redirect: () => {
                return 'charitable-remainder-trust-calculator';
            },
        },
        {
            path: '/model-clat-calculator/',
            redirect: () => {
                return 'clat-calculator';
            },
        },
        {
            path: '/model-crut-calculator/',
            redirect: () => {
                return 'charitable-remainder-trust-calculator';
            },
        },
        {
            path: '/oil-gas-calculator/',
            name: 'oil-gas-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorOilGas,
            meta: {
                title: 'Oil & Gas Financial Planning Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Plan oil & gas income, tax savings, and estate strategies. Optimize royalties ' +
                            "and mineral rights with Valur's calculator.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Plan oil & gas income, tax savings, and estate strategies. Optimize royalties ' +
                            "and mineral rights with Valur's calculator.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/oil-gas-calculator.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Oil & Gas Financial Planning Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/oil-gas-calculator',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Oil & Gas Financial Planning Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Plan oil & gas income, tax savings, and estate strategies. Optimize royalties ' +
                            "and mineral rights with Valur's calculator.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/oil-gas-calculator.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/oil-gas-calculator',
                    },
                ],
            },
        },
        {
            path: '/ppli-calculator/',
            name: 'ppli-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorPpli,
            meta: {
                title: 'Private Placement Life Insurance Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Model Private Placement Life Insurance benefits. Protect assets and grow ' +
                            "wealth tax-efficiently with Valur's calculator.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Model Private Placement Life Insurance benefits. Protect assets and grow ' +
                            "wealth tax-efficiently with Valur's calculator.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/ppli-calculator.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Private Placement Life Insurance Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/ppli-calculator',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Private Placement Life Insurance Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Model Private Placement Life Insurance benefits. Protect assets and grow ' +
                            "wealth tax-efficiently with Valur's calculator.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/ppli-calculator.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/ppli-calculator',
                    },
                ],
            },
        },
        {
            // Public share link (see the CRUT -shared route for the pattern).
            path: '/qsbs-calculator/shared/:share_token([0-9a-fA-F-]{36})',
            name: 'qsbs-calculator-shared',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorQsbs,
            meta: {
                title: 'Qualified Small Business Stock Calculator | Valur Tax Planning Calculator',
                metaTags: [{ name: 'robots', content: 'noindex' }],
            },
        },
        {
            path: '/qsbs-calculator/:scenario_id([0-9a-fA-F-]{36})?',
            name: 'qsbs-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorQsbs,
            meta: {
                title: 'Qualified Small Business Stock Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Estimate Section 1202 QSBS exclusion. Maximize capital gains tax savings ' +
                            "with Valur's free calculator.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Estimate Section 1202 QSBS exclusion. Maximize capital gains tax savings ' +
                            "with Valur's free calculator.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/qsbs-calculator.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Qualified Small Business Stock Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/qsbs-calculator',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Qualified Small Business Stock Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Estimate Section 1202 QSBS exclusion. Maximize capital gains tax savings ' +
                            "with Valur's free calculator.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/qsbs-calculator.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/qsbs-calculator',
                    },
                ],
            },
        },
        {
            path: '/solar-calculator/',
            name: 'solar-calculator',
            beforeEnter: calculatorRouteGuard,
            component: CalculatorSolar,
            meta: {
                title: 'Solar Savings Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Estimate solar savings, tax credits, and ROI. Plan your clean energy ' +
                            "investment with Valur's free calculator.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Estimate solar savings, tax credits, and ROI. Plan your clean energy ' +
                            "investment with Valur's free calculator.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/solar-calculator.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Solar Savings Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/solar-calculator',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Solar Savings Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Estimate solar savings, tax credits, and ROI. Plan your clean energy ' +
                            "investment with Valur's free calculator.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/solar-calculator.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/solar-calculator',
                    },
                ],
            },
        },

        // Onboarding
        {
            path: '/onboarding-details/:onboarding_id/',
            name: 'onboarding-details',
            component: OnboardingDetails,
            meta: { title: 'Onboarding details' },
        },
        {
            path: '/onboarding-form/:onboarding_id/',
            name: 'onboarding-form',
            component: OnboardingForm,
            meta: { title: 'Onboarding profile' },
        },
        {
            path: '/onboarding-initiate/:product_label/',
            name: 'onboarding-initiate',
            component: OnboardingInitiate,
            meta: { title: 'Onboarding initiation' },
        },
        {
            path: '/onboarding-strategy/:user_id?',
            name: 'onboarding-strategy',
            component: OnboardingStrategy,
            meta: { title: 'Onboarding strategy' },
            async beforeEnter(to) {
                // Strategy catalog is for logged-in users only. Gated here explicitly
                // as defense-in-depth alongside the global needsLogin() guard, and
                // because this route also needs the advisor select-user redirect below.
                if (!store.getters.isLoggedIn) {
                    return { name: 'login', query: { next: to.path } };
                }
                const userId = to?.params?.user_id;
                if (!userId && store.getters.isAdvisor) {
                    return {
                        name: 'select-user',
                        query: { target_name: 'onboarding-strategy' },
                    };
                }
                // Prefetch products before entering route for instant load
                await store.dispatch('configuration/prefetchProducts');
            },
        },
        {
            path: '/oil-gas-questionnaire/',
            name: 'oil-gas-questionnaire',
            component: OilGasQuestionnaire,
            meta: {
                title: 'Investment Questionnaire',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/oil-gas-questionnaire',
                    },
                ],
            },
        },
        {
            path: '/risk-analysis/',
            name: 'risk-analysis',
            component: RiskAnalysis,
            meta: {
                title: 'Risk analysis',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/risk-analysis',
                    },
                ],
            },
        },
        {
            // Remove this legacy redirect after 2027-03-29.
            path: '/solar-activities/',
            redirect: { name: 'material-participation-log' },
        },
        {
            path: '/material-participation-log/',
            name: 'material-participation-log',
            component: MaterialParticipationLog,
            meta: {
                title: 'Material participation log',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/material-participation-log',
                    },
                ],
            },
        },
        {
            path: '/solar-projects/:onboarding_id?/',
            name: 'solar-projects',
            component: SolarProjects,
            meta: {
                title: 'Solar Project ROI Calculator | Valur Tax Planning Calculator',
                metaTags: [
                    {
                        name: 'description',
                        content:
                            'Analyze solar project returns, tax credits, and cash flow. Plan profitable ' +
                            "clean energy projects with Valur's calculator.",
                    },
                    {
                        name: 'robots',
                        content: 'all',
                    },

                    {
                        property: 'og:description',
                        content:
                            'Analyze solar project returns, tax credits, and cash flow. Plan profitable ' +
                            "clean energy projects with Valur's calculator.",
                    },
                    {
                        property: 'og:image',
                        content: 'https://app.valur.com/og-images/solar-projects.png',
                    },
                    {
                        property: 'og:title',
                        content: 'Solar Project ROI Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: 'https://app.valur.com/solar-projects',
                    },

                    {
                        name: 'twitter:card',
                        content: 'summary_large_image',
                    },
                    {
                        name: 'twitter:title',
                        content: 'Solar Project ROI Calculator | Valur Tax Planning Calculator',
                    },
                    {
                        name: 'twitter:description',
                        content:
                            'Analyze solar project returns, tax credits, and cash flow. Plan profitable ' +
                            "clean energy projects with Valur's calculator.",
                    },
                    {
                        property: 'twitter:image',
                        content: 'https://app.valur.com/og-images/solar-projects.png',
                    },
                ],
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/solar-projects',
                    },
                ],
            },
        },
        {
            path: '/solar-projects-saved/',
            name: 'solar-projects-saved',
            component: SolarProjectsSaved,
            meta: {
                title: 'Saved solar projects',
                links: [
                    {
                        rel: 'canonical',
                        href: 'https://app.valur.com/solar-projects-saved',
                    },
                ],
            },
        },
        {
            path: '/solar-project-details/:slug/:onboarding_id?/',
            name: 'solar-project-details',
            component: SolarProjectDetails,
            props: true,
            meta: { title: 'Solar project details' },
        },

        {
            path: '/admin/create-distribution-report/:user_id/:onboarding_id',
            name: 'admin-create-distribution-report',
            component: AdminCreateDistributionReport,
            meta: { title: 'Distribution report' },
            // Ops-only builder (saves Valur-managed reports onto the client's trust).
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && user.is_superuser)) {
                    return '/';
                }
            },
        },
        {
            // Old landings kept as redirects so existing links/bookmarks survive: the
            // section's own address, and the list's until ENG-3439 renamed it Users.
            path: '/operations/',
            redirect: '/operations/users/',
        },
        {
            path: '/operations/clients/',
            redirect: '/operations/users/',
        },
        {
            // Every staff account, as in Area 51; what each may do to whom is the
            // server's to say, row by row (ENG-3439).
            path: '/operations/users/',
            name: 'operations',
            component: Operations,
            meta: { title: 'Operations - Users' },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && (user.is_superuser || user.is_staff))) {
                    return '/';
                }
            },
        },
        {
            path: '/operations/partner-usage/',
            name: 'operations-partner-usage',
            component: PartnerUsage,
            meta: { title: 'Operations - Partners', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && user.is_superuser)) {
                    return '/';
                }
            },
        },
        {
            path: '/operations/platform-settings/',
            name: 'operations-platform-settings',
            component: PlatformSettings,
            meta: { title: 'Operations - Platform settings', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && user.is_superuser)) {
                    return '/';
                }
            },
        },
        {
            path: '/operations/calculators-dashboard/',
            name: 'operations-calculators-dashboard',
            component: CalculatorsDashboard,
            meta: { title: 'Operations - Calculators', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && user.is_superuser)) {
                    return '/';
                }
                if (!isFeatureEnabled(FLAG_CALCULATORS_OPS_DASHBOARD)) {
                    return '/';
                }
            },
        },
        {
            // Two ops tools, two addresses. This one held /operations/form-5227-review/ until it grew
            // past Form 5227 — it now checks Form 56, the 709, both e-file authorizations and the Valur
            // 8283 attachment, so the name was the last thing still claiming otherwise (ENG-2923).
            // ENG-2894's extraction surfaces sit at /operations/form-5227-extraction/. The old path is
            // now claimed by neither; nothing redirects it.
            path: '/operations/document-validation/',
            name: 'operations-document-validation',
            component: DocumentValidationReview,
            meta: { title: 'Operations - Document validation', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && user.is_superuser)) {
                    return '/';
                }
                if (!isFeatureEnabled(FLAG_OPS_DOCUMENT_VALIDATION)) {
                    return '/';
                }
            },
        },
        {
            path: '/operations/form-5227-extraction/',
            name: 'form-5227-review',
            component: Form5227ReviewQueue,
            meta: { title: 'Operations - Form 5227 Extraction', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && (user.is_superuser || user.is_staff))) {
                    return '/';
                }
                if (!isFeatureEnabled(FLAG_OPS_FORM_5227_EXTRACTION)) {
                    return '/';
                }
            },
        },
        {
            path: '/operations/form-5227-extraction/upload/',
            name: 'form-5227-review-upload',
            component: Form5227ReviewUpload,
            meta: { title: 'Operations - Import Form 5227 Returns', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && (user.is_superuser || user.is_staff))) {
                    return '/';
                }
                if (!isFeatureEnabled(FLAG_OPS_FORM_5227_EXTRACTION)) {
                    return '/';
                }
            },
        },
        {
            path: '/operations/form-5227-extraction/:recordId/',
            name: 'form-5227-review-record',
            component: Form5227ReviewWorkspace,
            meta: { title: 'Operations - Form 5227 Extraction', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && (user.is_superuser || user.is_staff))) {
                    return '/';
                }
                if (!isFeatureEnabled(FLAG_OPS_FORM_5227_EXTRACTION)) {
                    return '/';
                }
            },
        },
        {
            // Custodian statements (ENG-2999): the extraction app's second document family, with
            // its own flag so it can roll out to reviewers independently of the 5227 tool.
            path: '/operations/custodian-statements/',
            name: 'custodian-statements',
            component: StatementReviewQueue,
            meta: { title: 'Operations - Custodian Statements', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && (user.is_superuser || user.is_staff))) {
                    return '/';
                }
                if (!isFeatureEnabled(FLAG_OPS_CUSTODIAN_STATEMENTS)) {
                    return '/';
                }
            },
        },
        {
            path: '/operations/custodian-statements/upload/',
            name: 'custodian-statements-upload',
            component: StatementReviewUpload,
            meta: { title: 'Operations - Import Custodian Statements', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && (user.is_superuser || user.is_staff))) {
                    return '/';
                }
                if (!isFeatureEnabled(FLAG_OPS_CUSTODIAN_STATEMENTS)) {
                    return '/';
                }
            },
        },
        {
            path: '/operations/custodian-statements/:recordId/',
            name: 'custodian-statements-record',
            component: StatementReviewWorkspace,
            meta: { title: 'Operations - Custodian Statements', requiresAdmin: true },
            beforeEnter() {
                const user = store.getters.user;
                if (!(user && (user.is_superuser || user.is_staff))) {
                    return '/';
                }
                if (!isFeatureEnabled(FLAG_OPS_CUSTODIAN_STATEMENTS)) {
                    return '/';
                }
            },
        },
        {
            path: '/admin/tax-table-generator/',
            name: 'admin-tax-table-generator',
            component: AdminTaxTableGenerator,
            meta: { title: 'Tax Table Generator' },
        },
        {
            // Old paths kept as redirects so existing links survive: the old admin
            // path, and the profile's own until ENG-3439 renamed the list Users.
            path: '/admin/user-profile/:user_id',
            redirect: to => `/operations/users/${to.params.user_id}/`,
        },
        {
            path: '/operations/clients/:user_id/',
            redirect: to => `/operations/users/${to.params.user_id}/`,
        },
        {
            path: '/operations/users/:user_id/',
            name: 'admin-user-profile',
            component: AdminUserProfile,
            meta: { title: 'User Profile' },
            async beforeEnter(to) {
                // Admins see every profile; advisors only their attached clients;
                // everyone else is bounced. The advisor check probes the accounts
                // endpoint (403 for unattached clients) BEFORE entering: the page's
                // own fetches run through logApiError, whose blanket 401/403
                // handling would log the advisor out entirely.
                if (!store.getters.isLoggedIn) {
                    return { name: 'login', query: { next: to.path } };
                }
                const user = store.getters.user;
                if (user && (user.is_superuser || user.is_staff)) {
                    return;
                }
                if (!store.getters.isAdvisor) {
                    return '/';
                }
                try {
                    await axios.get(`${API_ROOT}/accounts/${to.params.user_id}/`, store.getters.axiosConfig);
                } catch {
                    eventBus.emit('notification::error', "You don't have access to this client's profile.");
                    return { name: 'advisor-client-management' };
                }
            },
        },
        {
            path: '/admin/user-profile/:user_id/:onboarding_id/send-trust-agreement-email/',
            name: 'admin-onboarding-trust-agreement-email',
            component: AdminOnboardingTrustAgreementEmail,
            meta: { title: 'Compose Trust Agreement Email' },
        },
        {
            path: '/admin/user-profile/:user_id/:onboarding_id/edit-trust-agreement/',
            name: 'admin-onboarding-edit-trust-agreement',
            component: AdminOnboardingEditTrustAgreement,
            meta: { title: 'Edit Trust Agreement' },
        },
    ],
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        try {
            document.getElementById('top-nav').scrollIntoView();
        } catch (e) {
            // do nothing
        }

        if (from.meta?.transitionAnimation) {
            return new Promise(resolve =>
                setTimeout(
                    () => resolve({ left: 0, top: 0 }),
                    ANIMATION_LENGTHS[from.meta?.transitionAnimation.animationDuration],
                ),
            );
        }

        return { left: 0, top: 0 };
    },
});

export const needsAdvisor = to => {
    if (store.getters.isAdvisor) return false;
    // Lapsed-trial advisors keep read-only access to the advisor dashboard (its
    // actions render disabled) so they can see what the trial gave them; every
    // other advisor-* route stays advisor-only.
    if (store.getters.isTrialLapsedAdvisor && to.name === 'advisor-dashboard') return false;
    return to.name.indexOf('advisor-') === 0;
};

export const needsLogin = to => {
    const loggedIn = store.getters.isLoggedIn;
    return (
        to !== '/' &&
        to !== '/forgot-password' &&
        to.name !== 'activate' &&
        to.name !== 'calculators' &&
        to.name !== 'charitable-remainder-trust-calculator' &&
        to.name !== 'charitable-remainder-trust-calculator-shared' &&
        to.name !== 'charitable-remainder-trust-report' &&
        to.name !== 'clat-calculator' &&
        to.name !== 'clat-calculator-shared' &&
        to.name !== 'clear' &&
        to.name !== 'compare-estate-tax-savings' &&
        to.name !== 'compare-capital-gains-tax-savings' &&
        to.name !== 'compare-ordinary-income-tax-savings' &&
        to.name !== 'dashboard' &&
        to.name !== 'dashboard-v2' &&
        to.name !== 'forgot-password' &&
        to.name !== 'grat-calculator' &&
        to.name !== 'grat-calculator-shared' &&
        to.name !== 'ngt-calculator' &&
        to.name !== 'estate-tax-burn-calculator' &&
        to.name !== 'guided-planner' &&
        to.name !== 'guided-planner-flow' &&
        to.name !== 'home' &&
        to.name !== 'ira-calculator' &&
        to.name !== 'login' &&
        to.name !== 'model-calculator' &&
        to.name !== 'model-clat-calculator' &&
        to.name !== 'model-crut-calculator' &&
        to.name !== 'model-ira-calculator' &&
        to.name !== 'password-reset' &&
        to.name !== 'privacy-policy' &&
        to.name !== 'oil-gas-calculator' &&
        to.name !== 'ppli-calculator' &&
        to.name !== 'qsbs-calculator' &&
        to.name !== 'qsbs-calculator-shared' &&
        to.name !== 'resend-activation' &&
        to.name !== 'material-participation-log' &&
        to.name !== 'solar-calculator' &&
        to.name !== 'solar-projects' &&
        to.name !== 'solar-projects-saved' &&
        to.name !== 'solar-project-details' &&
        to.name !== 'terms-of-service' &&
        (to.name || '').indexOf('register') !== 0 &&
        !loggedIn
    );
};

router.beforeEach((to, from) => {
    store.dispatch('transition_animation/updateTransitionAnimation', {
        from: from.meta?.transitionAnimation,
        to: to.meta?.transitionAnimation,
    });
});

router.beforeEach(async to => {
    const solarMarketplaceRoutes = ['solar-projects', 'solar-projects-saved', 'solar-project-details'];

    if (solarMarketplaceRoutes.includes(to.name)) {
        // On a hard page load this guard can run before refreshJWT has
        // rehydrated the session (the initial navigation starts at
        // app.use(router), not at mount) — a logged-in consented user would
        // then be misclassified as anonymous and trapped in the required ToS
        // modal via ?needsConsent=true. Wait for the session to settle first.
        await sessionRehydrated;

        const loggedIn = store.getters.isLoggedIn;
        const requiresAuth = needsLogin(to);

        if (requiresAuth && !loggedIn) {
            return;
        }

        const { needsSolarTosConsent } = await import('@/utils/solar-tos-consent');
        const user = store.getters.user;

        if (needsSolarTosConsent(user) && to.query.needsConsent !== 'true') {
            return { name: to.name, params: to.params, query: { ...to.query, needsConsent: 'true' } };
        }
    }
});

// NOT debounced: a debounced guard returns undefined synchronously, which
// vue-router 4 treats as "allow", so it never awaits the login/advisor/oil-gas
// gate below. It must run as a normal awaited next-style guard to actually block.
router.beforeEach(async (to, from, next) => {
    if (to.name === 'clear') {
        next();
        return;
    }

    // Enforce the user's own Site: if they belong to a partner subdomain, send them back
    // to it whenever they land on a different host. The rules live in
    // `services/sites.js` (staff bypass, no-Site cases, hostname normalization);
    // impersonation is checked here because it's a store getter, and it skips the guard
    // so admins can browse as the target user.
    //
    // Reads `state.auth.user` rather than the `user` getter deliberately — the getter
    // returns the impersonated user, and it is the *real* account's Site that governs.
    const authUser = store.state.auth.user || {};
    const userSiteDomain = store.getters.isImpersonating ? '' : siteRedirectTarget(authUser, window.location.hostname);
    if (store.getters.isLoggedIn && userSiteDomain) {
        // A handover, not a fork: `handOffToSite` signs out here on the way, so this
        // host is left with no session to restore. Leaving one behind is what made the
        // main app unreachable for every later visitor to this browser (ENG-2839).
        await handOffToSite({
            href: window.location.href,
            target: userSiteDomain,
            logout: () => store.dispatch('logout'),
            navigate: url => {
                window.location.href = url;
            },
        });
        return;
    }

    if (store.getters.isLoggedIn && PRODUCTS_VERSION_POLL_ROUTES.some(re => re.test(to.path))) {
        store.dispatch('configuration/checkProductsVersion');
    }

    // Brand and workspace both follow the host (the workspace falls back to the membership
    // only for an advisor whose firm has no live subdomain). The action commits both; it
    // returns the *host* profile, which is what hit-counting is about — a membership
    // fallback on the main app is not a subdomain visit.
    const host_partner_profile = await store.dispatch('marketing/syncAdvisoryPartnerProfile');
    const host_partner_name = host_partner_profile?.name;
    if (host_partner_name && host_partner_name.toLowerCase().indexOf('valur') === -1 && !hasTrackedAdvisoryPartner) {
        hasTrackedAdvisoryPartner = true;
        trackAdvisoryPartnerHit(host_partner_name);
    }

    let advisory_partner_profile = store.getters['marketing/advisory_partner_profile'];

    const loggedIn = store.getters.isLoggedIn;
    if (loggedIn) {
        const isAdvisor = store.getters.isAdvisor;
        const isImpersonating = store.getters.isImpersonating;
        if (isAdvisor) {
            if (isImpersonating) {
                // The sync above resolved the admin's own workspace, so swap in the
                // impersonated advisor's (populated by UserImpersonationSerializer). The
                // workspace only: branding stays the host's.
                advisory_partner_profile = store.getters.user.advisory_partner;
                if (advisory_partner_profile) {
                    store.commit('marketing/updateAdvisoryPartnerProfile', advisory_partner_profile);
                }
            }
            if (advisory_partner_profile?.uuid) {
                const advisor_profile = await store.dispatch(
                    'marketing/getAdvisorProfile',
                    advisory_partner_profile.uuid,
                );
                store.commit('marketing/updateAdvisorProfile', advisor_profile);
            }
        }
    }
    // A tenant setting, so the host's — the same source as the sidebar and home page
    // (advisory_partner_mixin), never the membership fallback.
    const isOilGasEnabled = store.getters['marketing/brand_profile']?.is_oil_gas_questionnaire_enabled || false;
    let isOilGasCompleted = false;
    if (isOilGasEnabled && loggedIn) {
        try {
            let oilGasInfo = await store.dispatch('questionnaire/getOilGasInfo');
            if (Array.isArray(oilGasInfo)) {
                oilGasInfo = oilGasInfo[0];
            }
            isOilGasCompleted = !!oilGasInfo?.is_completed;
        } catch {
            // API failure shouldn't block navigation
            isOilGasCompleted = true;
        }
    }
    const oilGasExemptRoutes = [
        'oil-gas-questionnaire',
        'profile',
        'login',
        'activate',
        'forgot-password',
        'password-reset',
        'clear',
        'home',
        'privacy-policy',
        'terms-of-service',
        'resend-activation',
        'website-disclosure',
    ];
    const isOilGasExempt = oilGasExemptRoutes.includes(to.name) || (to.name || '').indexOf('register') === 0;

    // Mandatory onboarding gate: a logged-in user who hasn't completed onboarding is forced
    // to the wizard on every navigation (including manual URL entry), except on the wizard
    // itself and auth/legal/exit routes. Existing users were backfilled to
    // onboarding_complete=True (accounts migration 0080), so only genuinely-new users are gated.
    // `=== false` (not `!complete`) so an as-yet-unloaded user object never triggers a redirect.
    // On the initial navigation (hard page load / manual URL entry) this guard can win
    // the race against the bootstrap session-restore (main.js), running before the user
    // is populated; await the (deduped) refresh so a not-yet-onboarded user can't bypass
    // the gate by entering a URL directly. Scoped to START_LOCATION so logged-out
    // client-side browsing doesn't re-trigger a refresh on every navigation.
    if (from === START_LOCATION && !store.getters.user.id) {
        await store.dispatch('refreshJWT');
    }
    const currentUser = store.getters.user;
    // Computed only after the session restore above: on a hard page load these
    // would otherwise read pre-hydration state and detour a logged-in user
    // through /login, which re-enters the deep link without its query string.
    const needsToBeAdvisor = needsAdvisor(to);
    const needsToLogin = needsLogin(to);
    const needsOnboarding = store.getters.isLoggedIn && currentUser.onboarding_complete === false;
    const onboardingExemptRoutes = [
        'onboarding',
        'login',
        'activate',
        'clear',
        'password-reset',
        'forgot-password',
        'resend-activation',
        'privacy-policy',
        'terms-of-service',
    ];
    const isOnboardingExempt = onboardingExemptRoutes.includes(to.name) || (to.name || '').indexOf('register') === 0;

    if (needsToLogin) {
        // fullPath, not path: keep the query string so the post-login
        // redirect re-enters the deep link intact.
        next({ name: 'login', query: { next: to.fullPath } });
    } else if (needsOnboarding && !isOnboardingExempt) {
        next({ name: 'onboarding' });
    } else if (needsToBeAdvisor) {
        next({ name: 'dashboard' });
    } else if (isOilGasEnabled && !isOilGasExempt && !isOilGasCompleted) {
        next({ name: 'oil-gas-questionnaire' });
    } else {
        try {
            next();
        } catch (e) {
            handleGlobalError(e);
        }
        const tags = new Set();
        const links = new Set();
        if (to.meta) {
            if (to.meta.title) {
                document.title = to.meta.title;
            }

            let appendDefaultDescription = false;
            if ((to.meta.metaTags || []).length === 0) {
                appendDefaultDescription = true;
            } else {
                if (to.meta.metaTags.filter(e => e.name === 'description').length === 0) {
                    appendDefaultDescription = true;
                }
            }
            if (appendDefaultDescription) {
                to.meta.metaTags = [
                    {
                        name: 'description',
                        content:
                            'Valur is the simplest way to identify, set up, and take care of optimized tax ' +
                            'and estate-planning structures typically used by the ultrawealthy.',
                    },
                ];
            }

            (to.meta.metaTags || []).forEach(tag => {
                const identifier = tag.name || tag.property;
                tags.add(identifier);
                let append = false;
                let node;

                if (tag.property) {
                    // Handle OpenGraph and other property-based meta tags
                    node = document.querySelector(`meta[property="${tag.property}"]`);
                    if (!node) {
                        node = document.createElement('meta');
                        node.setAttribute('data-valur', true);
                        append = true;
                    }
                    node.setAttribute('property', tag.property);
                } else {
                    // Handle name-based meta tags
                    node = document.querySelector(`meta[name="${tag.name}"]`);
                    if (!node) {
                        node = document.createElement('meta');
                        node.setAttribute('data-valur', true);
                        append = true;
                    }
                    node.setAttribute('name', tag.name);
                }

                node.content = tag.content;
                if (append) {
                    document.querySelector('head').appendChild(node);
                }
            });
            (to.meta.links || []).forEach(link => {
                links.add(link.rel);
                let append = false;
                let node = document.querySelector(`link[rel=${link.rel}]`);
                if (!node) {
                    node = document.createElement('link');
                    node.setAttribute('data-valur', true);
                    append = true;
                }
                node.rel = link.rel;
                node.href = link.href;
                if (append) {
                    document.querySelector('head').appendChild(node);
                }
            });
        } else {
            document.title = 'Valur';
        }
        document.querySelectorAll('meta[data-valur=true]').forEach(node => {
            const identifier = node.getAttribute('name') || node.getAttribute('property');
            if (!tags.has(identifier)) {
                document.querySelector('head').removeChild(node);
            }
        });
        document.querySelectorAll('link[data-valur=true]').forEach(node => {
            if (!links.has(node.rel)) {
                document.querySelector('head').removeChild(node);
            }
        });
    }
});

// Track page views with PostHog (App Page Viewed event)
router.afterEach((to, from, failure) => {
    // afterEach still runs for aborted/cancelled navigations, and the calculators' unsaved-run
    // guard aborts one every time the user backs out of the prompt. Without this the user is
    // credited with viewing a page they never reached.
    if (failure) return;
    // Use nextTick to ensure page title is updated
    nextTick(() => {
        try {
            trackPageView({
                name: to.name,
                path: to.path,
            });
        } catch (e) {
            // Silent failure - tracking should never break user flow
        }
    });
});

// Handle chunk load errors during navigation (stale deployment)
router.onError(err => {
    handleGlobalError(err);
});

export default router;
